// CampaignsTab.tsx — Research → Campaigns (research track R1–R3; VMS-RESEARCH-CAMPAIGN-UI §1): an intent → chips → a
// harvest → candidates → the promote queue → the register → reproduced videos. Six panels; 80/20 — thumbnails, chips
// and counts, one line of text per row. Every count comes from ONE backend read (researchCampaignsApi.screen).
import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import type { CampaignKeyword, CampaignSummary, ResearchCampaignScreen, PromoteQueueEntry } from "@vms/contracts";
import { researchCampaignsApi } from "@/api/research-campaigns.ts";
import { researchApi, type ManagedNiche } from "@/api/research.ts";
import {
  chipRows, mapsToTag, funnelStages, workerBanner, fpvLabel, fpvTag, compact, craftLine, craftKey, defaultSelection,
} from "./campaign-view.ts";

const card: CSSProperties = { border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, background: "#fff" };
const btn: CSSProperties = { padding: "5px 10px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 12 };
const primary: CSSProperties = { ...btn, background: "#111827", color: "#fff", border: "1px solid #111827" };
const chip: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 999, fontSize: 12, border: "1px solid #d1d5db", background: "#f9fafb" };
const h2: CSSProperties = { fontSize: 13, fontWeight: 700, margin: "0 0 8px", textTransform: "uppercase", letterSpacing: 0.4, color: "#374151" };
const muted: CSSProperties = { color: "#6b7280", fontSize: 12 };
const thumb: CSSProperties = { width: "100%", aspectRatio: "9 / 16", objectFit: "cover", borderRadius: 8, background: "#e5e7eb", display: "block" };

export function CampaignsTab() {
  const [list, setList] = useState<CampaignSummary[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const reload = useCallback(() => researchCampaignsApi.list().then((r) => setList(r.campaigns)).catch((e) => setErr(e.message)), []);  // catch-reason: the error is shown to the person (setErr)
  useEffect(() => { void reload(); }, [reload]);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 14 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <NewCampaign onCreated={(id) => { void reload(); setOpen(id); }} />
        {err && <div style={{ ...muted, color: "#b91c1c" }}>{err}</div>}
        {(list ?? []).map((c) => (
          <button key={c.id} onClick={() => setOpen(c.id)} style={{ ...btn, textAlign: "left", background: open === c.id ? "#eef2ff" : "#fff" }}>
            <div style={{ fontWeight: 600 }}>{c.vertical.replace(/_/g, " ")}</div>
            <div style={muted}>{c.purpose.replace(/_/g, " ")} · {c.status}</div>
          </button>
        ))}
      </div>
      {open ? <CampaignScreen id={open} key={open} /> : <div style={muted}>Pick or create a campaign.</div>}
    </div>
  );
}

// ── 1. New campaign — one line: purpose, vertical, persona, target ────────────────────────────────────────────────
function NewCampaign({ onCreated }: { onCreated: (id: string) => void }) {
  const [niches, setNiches] = useState<ManagedNiche[]>([]);
  const [f, setF] = useState({ purpose: "audience_growth", vertical: "mexican_restaurant", ageBand: "25-34", gender: "any", interest: "food", target: 10 });
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => { researchApi.manageNiches().then(setNiches).catch(() => {}); }, []);
  const go = async () => {
    setBusy("…");
    try {
      const { campaign } = await researchCampaignsApi.create({ purpose: f.purpose as "audience_growth", vertical: f.vertical.trim(),
        persona: { ageBand: f.ageBand as "25-34", gender: f.gender as "any", interest: f.interest.trim() }, targetTeardowns: f.target });
      await researchCampaignsApi.generate(campaign.id);          // the recorded model expansion on top of the templates
      onCreated(campaign.id); setBusy(null);
    } catch (e) { setBusy((e as Error).message); }
  };
  const inp: CSSProperties = { ...btn, cursor: "text", width: "100%" };
  return (
    <div style={{ ...card, display: "grid", gap: 6 }}>
      <div style={h2}>New campaign</div>
      <select value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} style={inp}>
        <option value="audience_growth">audience growth</option><option value="sales">sales</option>
      </select>
      <input list="tf-niches" value={f.vertical} onChange={(e) => setF({ ...f, vertical: e.target.value })} style={inp} aria-label="vertical" />
      <datalist id="tf-niches">{niches.map((n) => <option key={n.id} value={n.vertical} />)}</datalist>
      <div style={{ display: "flex", gap: 4 }}>
        <select value={f.ageBand} onChange={(e) => setF({ ...f, ageBand: e.target.value })} style={inp}>
          {["13-17", "18-24", "25-34", "35-44", "45-54", "55+", "any"].map((a) => <option key={a}>{a}</option>)}</select>
        <select value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })} style={inp}>
          {["any", "female", "male"].map((a) => <option key={a}>{a}</option>)}</select>
      </div>
      <input value={f.interest} onChange={(e) => setF({ ...f, interest: e.target.value })} style={inp} aria-label="interest" />
      <button onClick={go} style={primary} disabled={busy === "…"}>Generate keywords</button>
      {busy && busy !== "…" && <div style={{ ...muted, color: "#b91c1c" }}>{busy}</div>}
    </div>
  );
}

function CampaignScreen({ id }: { id: string }) {
  const [s, setS] = useState<ResearchCampaignScreen | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => researchCampaignsApi.screen(id).then((x) => { setS(x); setErr(null); }).catch((e) => setErr(e.message)), [id]);  // catch-reason: the error is shown to the person (setErr)
  useEffect(() => { void load(); const t = setInterval(load, 15_000); return () => clearInterval(t); }, [load]);
  const act = (f: () => Promise<unknown>) => () => f().then(load).catch((e) => setErr((e as Error).message));  // catch-reason: the error is shown to the person (setErr)
  if (!s) return <div style={muted}>{err ?? "Loading…"}</div>;
  const c = s.campaign;
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{c.vertical.replace(/_/g, " ")} · {c.purpose.replace(/_/g, " ")}</div>
        <code style={muted}>{c.referenceSetId}</code>
        {err && <span style={{ ...muted, color: "#b91c1c" }}>{err}</span>}
      </div>
      {/* 80/20: a draft's work is its chips; once launched the pictures lead and the chips collapse to one line. */}
      {c.status === "draft" ? <><Keywords s={s} act={act} /><Board s={s} act={act} /></> : <>
        <Candidates s={s} />
        <PromoteQueue s={s} act={act} />
        <Register s={s} act={act} onChanged={load} />
        <Board s={s} act={act} />
        <Keywords s={s} act={act} />
      </>}
    </div>
  );
}
type Act = (f: () => Promise<unknown>) => () => void;

// ── 2. Keywords — the chips in rows; add / remove before launch ───────────────────────────────────────────────────
function Keywords({ s, act }: { s: ResearchCampaignScreen; act: Act }) {
  const c = s.campaign, draft = c.status === "draft";
  const [kw, setKw] = useState<CampaignKeyword[]>(c.keywords);
  useEffect(() => setKw(c.keywords), [c.keywords]);
  const dirty = JSON.stringify(kw) !== JSON.stringify(c.keywords);
  const [show, setShow] = useState(false);
  if (!draft && !show) return (
    <div style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span style={muted}>{s.board.keywords.total} keyword jobs from {c.keywords.length} chips</span>
      <button style={btn} onClick={() => setShow(true)}>Show chips</button>
    </div>);
  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={h2}>Keywords</div>
        {draft && <div style={{ display: "flex", gap: 6 }}>
          <button style={btn} onClick={act(() => researchCampaignsApi.generate(c.id))}>Expand</button>
          {dirty && <button style={btn} onClick={act(() => researchCampaignsApi.saveKeywords(c.id, kw))}>Save</button>}
          <button style={primary} disabled={dirty} onClick={act(() => researchCampaignsApi.launch(c.id))}>Launch</button>
        </div>}
      </div>
      {chipRows(kw).map((row) => (
        <div key={row.layer} style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center", margin: "4px 0" }}>
          <span style={{ ...muted, width: 64 }}>{row.label}</span>
          {row.chips.map((k) => (
            <span key={k.text} style={{ ...chip, ...(k.source === "model" ? { borderStyle: "dashed" } : {}), ...(row.layer === "negative" ? { background: "#fef2f2" } : {}) }}
              title={mapsToTag(k) ?? undefined}>
              {k.text}{mapsToTag(k) && <span style={{ ...muted, fontSize: 10 }}>{mapsToTag(k)!.split(" · ")[0]}</span>}
              {draft && <button aria-label={`remove ${k.text}`} onClick={() => setKw(kw.filter((x) => x !== k))} style={{ border: 0, background: "none", cursor: "pointer", padding: 0 }}>×</button>}
            </span>
          ))}
          {draft && <input placeholder="+" style={{ ...chip, width: 90 }} onKeyDown={(e) => {
            const v = (e.target as HTMLInputElement).value.trim();
            if (e.key === "Enter" && v) { setKw([...kw, { layer: row.layer, text: v, mapsTo: null, source: "operator" }]); (e.target as HTMLInputElement).value = ""; }
          }} />}
        </div>
      ))}
    </div>
  );
}

// ── 3. Harvest board — the funnel's claim state, never a guess ────────────────────────────────────────────────────
function Board({ s, act }: { s: ResearchCampaignScreen; act: Act }) {
  const b = s.board, c = s.campaign, banner = workerBanner(b, c.vertical, c.status);
  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={h2}>Harvest · {b.keywords.searched}/{b.keywords.total} keywords searched</div>
        <div style={{ display: "flex", gap: 6 }}>
          {c.status === "launched" && <button style={btn} onClick={act(() => researchCampaignsApi.pause(c.id))}>Pause</button>}
          {c.status === "paused" && <button style={btn} onClick={act(() => researchCampaignsApi.resume(c.id))}>Resume</button>}
          {b.funnel.kept > 0 && <button style={btn} title="re-read the kept creators every few days so follows-per-view can be measured"
            onClick={act(() => researchCampaignsApi.revisit(c.id))}>Revisit kept creators</button>}
        </div>
      </div>
      {banner && <div style={{ ...muted, color: "#92400e", marginBottom: 6 }}>{banner}</div>}
      <div style={{ display: "flex", gap: 6, alignItems: "flex-end", height: 96 }}>
        {funnelStages(b.funnel).map(([label, n], i, all) => {
          const max = Math.max(1, ...all.map(([, x]) => x));
          const h = n > 0 ? 12 + 60 * Math.log10(1 + n) / Math.log10(1 + max) : 4;   // log: 200 harvested beside 8 torn down both read
          return (
            <div key={label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch", gap: 2 }} title={`${n} ${label}`}>
              <div data-visual style={{ height: h, borderRadius: 6, background: i === 5 ? "#10b981" : "#9ca3af" }} />
              <div style={{ fontSize: 10, textAlign: "center", color: "#374151" }}>{n} {label}</div>
            </div>);
        })}
      </div>
      {b.workersHolding.map((w) => (
        <div key={w.workerId + w.keyword} style={{ ...muted, marginTop: 4 }}>
          ● {w.workerId} · {w.mode ?? "?"} · “{w.keyword}” · {Math.round(w.heartbeatAgeSec / 60)} min ago
        </div>
      ))}
    </div>
  );
}

// ── 4. Candidates — a contact sheet, sorted by follows-per-view ───────────────────────────────────────────────────
// (TF3-1, 10-14) "For a cover" (a covers clone of a candidate) is deleted with the clone board.
function Candidates({ s }: { s: ResearchCampaignScreen }) {
  return (
    <div style={card}>
      <div style={h2}>Candidates · {s.candidates.length} · by follows per 1k views <span style={{ textTransform: "none", fontWeight: 400 }}>(account = the account's conversion, not this video's)</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
        {s.candidates.map((c) => {
          const f = fpvLabel(c);
          return (
            <div key={c.id}>
              {/* the overlays sit on the IMAGE's box (the grid stretches cards in a row to the tallest one) */}
              <div style={{ position: "relative" }}>
                <a href={c.sourceUrl} target="_blank" rel="noreferrer" style={{ display: "block", lineHeight: 0 }}>
                  {c.thumbnailUrl ? <img data-visual src={c.thumbnailUrl} alt="" style={thumb} /> : <div data-visual style={thumb} />}
                </a>
                <div style={{ position: "absolute", top: 4, left: 4, right: 4, display: "flex", justifyContent: "space-between", fontSize: 11, color: "#fff", textShadow: "0 1px 2px #000" }}>
                  <span>{compact(c.views)} ▶</span><span>{compact(c.followers)} ♥</span>
                </div>
                <div style={{ position: "absolute", bottom: 4, left: 4, fontSize: 11, padding: "1px 6px", borderRadius: 999, color: "#fff",
                  background: f.tone === "measured" ? "#047857cc" : f.tone === "proxy" ? "#b45309cc" : "#6b7280aa" }} title={f.text}>{fpvTag(c)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 5. Promote queue — per teardown: the craft it extracted, the source frames beside it ─────────────────────────
function PromoteQueue({ s, act }: { s: ResearchCampaignScreen; act: Act }) {
  return (
    <div style={card}>
      <div style={h2}>Promote queue · {s.promoteQueue.length}</div>
      {s.promoteQueue.map((e) => <QueueEntry key={e.teardownJobId} e={e} campaignId={s.campaign.id} act={act} />)}
    </div>
  );
}
function QueueEntry({ e, campaignId, act }: { e: PromoteQueueEntry; campaignId: string; act: Act }) {
  const [sel, setSel] = useState<Set<string>>(() => defaultSelection(e));
  const picked = useMemo(() => e.crafts.filter((c) => sel.has(craftKey(c))).map((c) => ({ kind: c.kind, shotIndex: c.shotIndex })), [e, sel]);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 10, padding: "8px 0", borderTop: "1px solid #f3f4f6" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 3 }}>
        {e.frames.slice(0, 3).map((u) => <img key={u} data-visual src={u} alt="" style={{ ...thumb, borderRadius: 5 }} />)}
      </div>
      <div>
        {e.facetsMissing ? <div style={muted}>Torn down before the craft facets — re-tear it down to promote its craft.</div> : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {e.crafts.map((c) => {
                const k = craftKey(c), on = sel.has(k);
                return <button key={k} onClick={() => { const n = new Set(sel); on ? n.delete(k) : n.add(k); setSel(n); }}
                  style={{ ...chip, cursor: "pointer", background: on ? "#eef2ff" : "#fff", borderColor: on ? "#6366f1" : "#d1d5db" }}
                  title={c.defaulted.length ? `defaulted: ${c.defaulted.join(", ")}` : undefined}>{craftLine(c)}</button>;
              })}
            </div>
            <div style={{ ...muted, margin: "4px 0" }}>{e.delivery ? `delivery ${e.delivery}` : ""}{e.pattern ? ` · pattern ${e.pattern.storyPatternId.replace(/_/g, " ")}` : ""}</div>
            <button style={primary} disabled={!picked.length} onClick={act(() => researchCampaignsApi.promote(campaignId, e.teardownJobId, picked))}>
              Promote {picked.length} → candidates
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── 6. Register — the promoted items + brand style; reproduce a goal video (TF3-1: the cover is deleted) ─────────
function Register({ s, act, onChanged }: { s: ResearchCampaignScreen; act: Act; onChanged: () => void }) {
  const c = s.campaign;
  const [biz, setBiz] = useState(""); const [preset, setPreset] = useState(s.register.presets[0]?.id ?? "");
  const [msg, setMsg] = useState<string | null>(null);
  const bizOk = /^[0-9a-f-]{36}$/i.test(biz.trim());
  const run = (f: () => Promise<unknown>, done: string) => () => f().then(() => { setMsg(done); onChanged(); }).catch((e) => setMsg((e as Error).message));  // catch-reason: the error is shown to the person (setMsg)
  return (
    <div style={card}>
      <div style={h2}>Register · {s.register.brandStyleId ?? "the pack's styles"} · {s.register.items.filter((i) => i.status === "active").length} active / {s.register.items.length}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 8 }}>
        {s.register.items.map((i) => (
          <div key={i.id}>
            {i.frameUrl ? <img data-visual src={i.frameUrl} alt="" style={{ ...thumb, aspectRatio: "1 / 1" }} /> : <div data-visual style={{ ...thumb, aspectRatio: "1 / 1" }} />}
            <div style={{ fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={i.name}>{i.type.replace(/_/g, " ")}</div>
            {i.status === "candidate"
              ? <button style={{ ...btn, width: "100%" }} onClick={act(() => { const r = prompt("Why does this belong in the register?"); return r ? researchCampaignsApi.curate(c.id, i.id, r) : Promise.resolve(); })}>Curate</button>
              : <div style={{ ...muted, color: "#065f46" }}>active</div>}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
        <input value={biz} onChange={(e) => setBiz(e.target.value)} placeholder="the restaurant's business id" style={{ ...btn, cursor: "text", width: 290 }} />
        <select value={preset} onChange={(e) => setPreset(e.target.value)} style={btn}>{s.register.presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
        <button style={primary} disabled={!bizOk || !preset} onClick={run(() => researchCampaignsApi.reproduceGoal(c.id, biz.trim(), preset), "Goal video ordered.")}>Reproduce a goal video</button>
      </div>
      <div style={{ ...muted, marginTop: 6 }}>{s.board.runs.map((r) => `${r.kind} ${r.runId.slice(0, 8)}`).join(" · ") || "no reproductions yet"}{msg ? ` — ${msg}` : ""}</div>
    </div>
  );
}

/** One campaign's screen, opened directly (the 80/20 fixture page renders this). */
export function CampaignsTabOpen({ id }: { id: string }) { return <CampaignScreen id={id} />; }
